import { useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, XCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PageShell } from "@/components/AppShell";

const Intro = ({ onStart, busy }) => (
  <div data-testid="exec-check-intro">
    <h1 className="mt-2 font-heading text-4xl font-black tracking-tighter">Leader quiz</h1>
    <p className="mt-3 max-w-2xl text-muted-foreground">10 scenario questions drawn from the 47, with at least one from each of the nine sessions. Pass at 8 of 10. Each retake draws a new mix.</p>
    <button type="button" onClick={onStart} disabled={busy} data-testid="exec-check-start" className="mt-8 rounded-sm bg-brand-action px-6 py-3 font-semibold text-brand-action-fg disabled:opacity-50">Start the check</button>
  </div>
);

const Question = ({ q, i, value, onPick }) => (
  <fieldset data-testid={`exec-q-${i}`} className="border-b border-border py-6">
    <legend className="font-semibold"><span className="mr-2 font-mono text-xs text-muted-foreground">{i + 1}/10</span>{q.stem}</legend>
    <div className="mt-4 grid gap-2">
      {q.options.map((o) => (
        <button key={o.id} type="button" data-testid={`exec-q-${i}-opt-${o.id}`} onClick={() => onPick(o.id)} className={`border p-3 text-left text-sm transition-colors ${value === o.id ? "border-brand-primary bg-brand-primary/10 font-semibold" : "border-border hover:bg-muted"}`}>
          <span className="mr-2 font-mono">{o.id}</span>{o.text}
        </button>
      ))}
    </div>
  </fieldset>
);

const Result = ({ r, onRetake }) => (
  <div data-testid="exec-check-result">
    <div data-testid="exec-check-score" className="font-heading text-7xl font-black tracking-tighter">{r.score}%</div>
    <p className="mt-2 text-muted-foreground">{r.correct} of {r.total} correct · pass mark 80%</p>
    {r.passed ? <p data-testid="exec-check-passed" className="mt-4 text-lg text-brand-accent">You passed the leader quiz.</p> : <p data-testid="exec-check-failed" className="mt-4 text-lg">Below 80% this time. Review the explanations below, then try again.</p>}
    {r.preview_only && <p data-testid="exec-check-preview-only" className="mt-3 text-sm text-muted-foreground">Admin preview: no certificate is issued for admin accounts.</p>}
    <div className="mt-6 flex flex-wrap gap-3">
      {r.certificate_id && <Link to={`/certificates/${r.certificate_id}`} data-testid="exec-check-certificate-link" className="rounded-sm bg-brand-action px-5 py-2.5 font-semibold text-brand-action-fg">View your Executive certificate →</Link>}
      <button type="button" onClick={onRetake} data-testid="exec-check-retake" className="rounded-sm border border-border px-5 py-2.5 font-semibold hover:bg-muted">Try again</button>
    </div>
    <ol className="mt-10 space-y-4">
      {r.review.map((q, i) => (
        <li key={q.id} data-testid={`exec-review-${i}`} className={`border-l-4 bg-card p-4 text-sm ${q.correct ? "border-brand-accent" : "border-destructive"}`}>
          <div className="flex gap-2 font-semibold">{q.correct ? <CheckCircle2 className="h-4 w-4 shrink-0 text-brand-accent" /> : <XCircle className="h-4 w-4 shrink-0 text-destructive" />}{q.stem}</div>
          <p className="mt-2">Best answer: <strong>{q.options.find((o) => o.id === q.answer).text}</strong>{!q.correct && <span className="text-muted-foreground"> · you chose: {q.options.find((o) => o.id === q.selected)?.text}</span>}</p>
          <p className="mt-1 text-muted-foreground">Why: {q.rationale}</p>
        </li>
      ))}
    </ol>
  </div>
);

export default function ExecutiveCheck() {
  const qc = useQueryClient();
  const [attempt, setAttempt] = useState(null);
  const [ans, setAns] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/executive/check/start");
      setAttempt(data); setAns({}); setResult(null);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/executive/check/${attempt.attempt_id}/submit`, { answers: ans });
      setResult(data); setAttempt(null);
      qc.invalidateQueries({ queryKey: ["executive"] });
      window.scrollTo(0, 0);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const answered = attempt ? attempt.questions.filter((q) => ans[q.id]).length : 0;
  return (
    <PageShell>
      <div className="mx-auto max-w-3xl">
        <div className="overline-label">Leader Briefing · assessment</div>
        {result ? <Result r={result} onRetake={start} /> : attempt ? (
          <div data-testid="exec-check-form">
            {attempt.questions.map((q, i) => <Question key={q.id} q={q} i={i} value={ans[q.id]} onPick={(v) => setAns({ ...ans, [q.id]: v })} />)}
            <div className="mt-8 flex items-center gap-4">
              <button type="button" onClick={submit} disabled={busy || answered < 10} data-testid="exec-check-submit" className="rounded-sm bg-brand-action px-6 py-3 font-semibold text-brand-action-fg disabled:opacity-45">Submit answers</button>
              <span className="font-mono text-xs">{answered}/10 answered</span>
            </div>
          </div>
        ) : <Intro onStart={start} busy={busy} />}
      </div>
    </PageShell>
  );
}
